import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgZone } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet.markercluster';

export type LeafletLatLng = { lat: number; lng: number };

@Component({
  selector: 'app-leaflet-map',
  templateUrl: './leaflet-map.component.html',
  styleUrl: './leaflet-map.component.scss',
})
export class LeafletMapComponent implements AfterViewInit, OnChanges, OnDestroy {
  @ViewChild('mapContainer', { static: false })
  mapContainer?: ElementRef<HTMLDivElement>;

  @Input() heightPx: number = 420;
  @Input() center: LeafletLatLng = { lat: 41.9, lng: 12.5 };
  @Input() zoom: number = 5;

  /** Se true, un click sulla mappa imposta `selectedPosition`. */
  @Input() selectable = false;
  @Input() selectedPosition: LeafletLatLng | null = null;
  @Input() markerDraggable = false;

  /** Marker multipli (es. vista home). */
  @Input() markers: Array<{
    id: string | number;
    position: LeafletLatLng;
    popupHtml?: string;
  }> = [];

  /** Se true, abilita il clustering per `markers`. */
  @Input() clusterMarkers = false;

  /**
   * Token usato per forzare `invalidateSize()` quando il contenitore
   * diventa visibile (es. dentro `p-dialog`).
   */
  @Input() resizeToken: unknown;

  @Output() selectedPositionChange = new EventEmitter<LeafletLatLng | null>();

  private map?: L.Map;
  private baseLayer?: L.TileLayer;
  private satelliteLayer?: L.TileLayer;
  isSatellite = false;
  private controls?: L.Control;
  private layerBtnEl?: HTMLButtonElement;
  private selectedMarker?: L.Marker;
  private markersLayer?: L.LayerGroup;
  private markerClusterLayer?: L.MarkerClusterGroup;
  private inited = false;
  private initAttempts = 0;
  private readonly defaultMarkerIcon = L.divIcon({
    className: 'refhub-marker',
    html: '<div class="refhub-marker-pin"></div><div class="refhub-marker-dot"></div>',
    iconSize: [42, 42],
    iconAnchor: [21, 42],
    popupAnchor: [0, -40],
  });

  constructor(private zone: NgZone) {}

  ngAfterViewInit(): void {
    this.initMapIfNeeded();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Se l'inizializzazione è stata rimandata (container non visibile),
    // riprova quando arrivano segnali di resize/visibilità.
    if (!this.inited) {
      if (changes['resizeToken'] || changes['heightPx']) {
        this.initMapIfNeeded();
      }
      return;
    }

    if (changes['resizeToken']) {
      // doppio invalidate: immediato + post animazione dialog
      setTimeout(() => this.map?.invalidateSize(), 0);
      setTimeout(() => this.map?.invalidateSize(), 250);
    }

    if (changes['heightPx']) {
      // size invalidation dopo resize container
      setTimeout(() => this.map?.invalidateSize(), 0);
    }

    if (changes['center'] || changes['zoom']) {
      this.map?.setView([this.center.lat, this.center.lng], this.zoom);
    }

    if (changes['selectedPosition']) {
      this.renderSelectedMarker();
    }

    if (changes['markers'] || changes['clusterMarkers']) {
      this.renderMarkersLayer();
    }
  }

  ngOnDestroy(): void {
    try {
      this.controls?.remove();
      this.map?.remove();
    } finally {
      this.map = undefined;
      this.selectedMarker = undefined;
      this.markersLayer = undefined;
      this.markerClusterLayer = undefined;
      this.inited = false;
      this.controls = undefined;
    }
  }

  private initMapIfNeeded(): void {
    if (this.inited) return;
    const el = this.mapContainer?.nativeElement;
    if (!el) return;

    // Se la mappa viene creata mentre il container è nascosto (display:none),
    // Leaflet calcola dimensioni 0 e rimane "grigia". Ritentiamo finché diventa visibile.
    if (el.clientWidth === 0 || el.clientHeight === 0) {
      if (this.initAttempts < 40) {
        this.initAttempts += 1;
        setTimeout(() => this.initMapIfNeeded(), 50);
      }
      return;
    }

    // Leaflet genera molti eventi (mousemove/scroll/drag). Per evitare che Angular
    // faccia change detection ad ogni evento, inizializziamo fuori dalla zone.
    this.zone.runOutsideAngular(() => {
      this.map = L.map(el, {
        zoomControl: false,
        attributionControl: false,
      }).setView([this.center.lat, this.center.lng], this.zoom);

      this.baseLayer = L.tileLayer(
        'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
        { maxZoom: 19 },
      );
      this.satelliteLayer = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        { maxZoom: 19 },
      );
      this.baseLayer.addTo(this.map);
      this.isSatellite = false;
      this.addControls();

      if (this.selectable) {
        this.map.on('click', (e: L.LeafletMouseEvent) => {
          const pos = { lat: e.latlng.lat, lng: e.latlng.lng };
          // rientra in Angular solo per aggiornare stato/app
          this.zone.run(() => {
            this.selectedPosition = pos;
            this.renderSelectedMarker();
            this.selectedPositionChange.emit(pos);
          });
        });
      }

      this.inited = true;
      this.renderSelectedMarker();
      this.renderMarkersLayer();

      // forza un recalcolo (utile dentro dialog/wizard)
      setTimeout(() => this.map?.invalidateSize(), 0);
      setTimeout(() => this.map?.invalidateSize(), 250);
    });
  }

  toggleLayer(): void {
    if (!this.map || !this.baseLayer || !this.satelliteLayer) return;
    this.zone.runOutsideAngular(() => {
      if (this.isSatellite) {
        this.map!.removeLayer(this.satelliteLayer!);
        this.baseLayer!.addTo(this.map!);
        this.isSatellite = false;
      } else {
        this.map!.removeLayer(this.baseLayer!);
        this.satelliteLayer!.addTo(this.map!);
        this.isSatellite = true;
      }
    });
    this.updateLayerButtonUi();
  }

  zoomIn(): void {
    if (!this.map) return;
    this.zone.runOutsideAngular(() => this.map!.zoomIn());
  }

  zoomOut(): void {
    if (!this.map) return;
    this.zone.runOutsideAngular(() => this.map!.zoomOut());
  }

  private addControls(): void {
    if (!this.map) return;
    if (this.controls) {
      this.controls.remove();
      this.controls = undefined;
    }

    const self = this;
    const Controls = L.Control.extend({
      options: { position: 'topright' },
      onAdd() {
        const container = L.DomUtil.create(
          'div',
          'refhub-map-controls leaflet-bar',
        ) as HTMLDivElement;
        container.style.border = 'none';
        container.style.background = 'transparent';
        container.style.boxShadow = 'none';

        const layerBtn = L.DomUtil.create('button', 'map-btn', container);
        layerBtn.type = 'button';
        self.layerBtnEl = layerBtn;
        self.updateLayerButtonUi();

        const zoomGroup = L.DomUtil.create('div', 'zoom-group', container);
        const zin = L.DomUtil.create('button', 'map-btn', zoomGroup);
        zin.type = 'button';
        zin.title = 'Zoom +';
        zin.innerHTML = `<i class="pi pi-plus"></i>`;

        const zout = L.DomUtil.create('button', 'map-btn', zoomGroup);
        zout.type = 'button';
        zout.title = 'Zoom -';
        zout.innerHTML = `<i class="pi pi-minus"></i>`;

        // Evita che i click sui bottoni trascinino/zoomino la mappa
        L.DomEvent.disableClickPropagation(container);
        L.DomEvent.disableScrollPropagation(container);

        layerBtn.addEventListener('click', () => self.toggleLayer());
        zin.addEventListener('click', () => self.zoomIn());
        zout.addEventListener('click', () => self.zoomOut());

        return container;
      },
    });

    this.controls = new Controls();
    this.controls.addTo(this.map);
  }

  private updateLayerButtonUi(): void {
    if (!this.layerBtnEl) return;
    const tip = this.isSatellite
      ? 'Torna alla mappa base'
      : 'Passa alla mappa satellitare';
    this.layerBtnEl.setAttribute('data-tip', tip);
    this.layerBtnEl.setAttribute('aria-label', tip);
    this.layerBtnEl.innerHTML = `<i class="pi ${
      this.isSatellite ? 'pi-map' : 'pi-image'
    }"></i>`;
  }

  private renderSelectedMarker(): void {
    if (!this.map) return;

    if (!this.selectedPosition) {
      if (this.selectedMarker) {
        this.selectedMarker.removeFrom(this.map);
        this.selectedMarker = undefined;
      }
      return;
    }

    const latLng: L.LatLngExpression = [
      this.selectedPosition.lat,
      this.selectedPosition.lng,
    ];

    if (!this.selectedMarker) {
      this.selectedMarker = L.marker(latLng, {
        draggable: this.markerDraggable,
        icon: this.defaultMarkerIcon,
      }).addTo(this.map);

      if (this.markerDraggable) {
        this.selectedMarker.on('dragend', () => {
          const ll = this.selectedMarker?.getLatLng();
          if (!ll) return;
          const pos = { lat: ll.lat, lng: ll.lng };
          this.zone.run(() => {
            this.selectedPosition = pos;
            this.selectedPositionChange.emit(pos);
          });
        });
      }
    } else {
      this.selectedMarker.setLatLng(latLng);
    }
  }

  private renderMarkersLayer(): void {
    if (!this.map) return;

    // clear previous
    this.markersLayer?.removeFrom(this.map);
    this.markerClusterLayer?.removeFrom(this.map);
    this.markersLayer = undefined;
    this.markerClusterLayer = undefined;

    if (!this.markers || this.markers.length === 0) return;

    if (this.clusterMarkers) {
      const cluster = (L as any).markerClusterGroup?.() as L.MarkerClusterGroup;
      if (!cluster) {
        // fallback senza clustering
        this.markersLayer = L.layerGroup().addTo(this.map);
        this.addMarkersToLayer(this.markersLayer);
        return;
      }

      this.markerClusterLayer = cluster;
      this.addMarkersToLayer(this.markerClusterLayer);
      this.markerClusterLayer.addTo(this.map);
      return;
    }

    this.markersLayer = L.layerGroup().addTo(this.map);
    this.addMarkersToLayer(this.markersLayer);
  }

  private addMarkersToLayer(layer: L.LayerGroup): void {
    for (const m of this.markers) {
      const marker = L.marker([m.position.lat, m.position.lng], {
        icon: this.defaultMarkerIcon,
      });
      if (m.popupHtml) {
        marker.bindPopup(m.popupHtml, { maxWidth: 340 });
      }
      marker.addTo(layer);
    }
  }
}

